import type { TFunction } from 'i18next'
import type { ServedItem } from './types'

/**
 * Formate une durée en `m:ss`, pour l'affichage du chronomètre et des écrans
 * de fin. Toujours arrondie à la seconde la plus proche, jamais négative.
 */
export function formatClock(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Consigne affichée pour un item, quel que soit son type. Ce champ ne révèle
 * jamais la réponse : il est donc toujours présent, y compris quand l'item a
 * été servi sans ses champs de correction (mode `daily`/`duel`, SPEC §8).
 */
export function itemPrompt(item: ServedItem): string {
  switch (item.type) {
    case 'qcm':
      return item.payload.question
    case 'vrai_faux':
      return item.payload.assertion
    case 'paire':
      // `left` porte la consigne pour `memory_definitions`/`appariement`,
      // mais c'est `right` (la définition donnée) pour `mot_juste` — lui
      // seul se voit amputer `left`, jamais `right` (voir
      // `Games/Support/PayloadRedactor.php::redactPaire()`, lot M2). Le
      // repli sur `right` couvre donc exactement ce cas ; `''` reste un
      // filet pour un payload qui n'aurait ni l'un ni l'autre.
      return item.payload.left ?? item.payload.right ?? ''
    case 'ordre':
      return item.payload.instruction
    case 'localisation':
      return item.payload.prompt
    case 'numerique':
      return item.payload.question
    case 'classement':
      return item.payload.instruction
    case 'texte_a_trous':
      return item.payload.template
  }
}

/**
 * Représentation lisible d'une valeur de correction, quel que soit le type de
 * l'item. `correctValue` vient toujours du serveur (`ItemCorrection.correct_value`,
 * SPEC §12) : c'est la seule source fiable en mode classé, où le payload de
 * l'item ne porte pas la réponse. `t` fournit les libellés d'interface
 * (« Vrai »/« Faux ») sans jamais écrire de texte en dur ici.
 */
export function describeCorrectValue(item: ServedItem, correctValue: unknown, t: TFunction): string {
  if (item.type === 'vrai_faux' && typeof correctValue === 'boolean') {
    return correctValue ? t('practice.common.true') : t('practice.common.false')
  }

  if (item.type === 'qcm' && Array.isArray(correctValue)) {
    const options = item.payload.options
    return correctValue
      .map((index) => (typeof index === 'number' ? (options[index] ?? String(index)) : String(index)))
      .join(', ')
  }

  if (Array.isArray(correctValue)) {
    return correctValue.map((entry) => String(entry)).join(', ')
  }

  if (correctValue === null || correctValue === undefined) {
    return ''
  }

  return String(correctValue)
}
